import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Share2, Download, Copy, Users, Award, QrCode, ChevronRight } from 'lucide-react';
import QRCode from 'qrcode';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import { User } from '../types';
import { getMyReferralCode, getMyReferrals, getReferralLeaderboard } from '../api';

interface Props {
  currentUser: User;
  notificationBell?: React.ReactNode;
  onOpenCandidate?: (id: string) => void;
}

const STAGE_COLORS: Record<string, string> = {
  'new': 'bg-gray-100 text-gray-600',
  'screening': 'bg-primary-100 text-primary-700',
  'screening_passed': 'bg-primary-100 text-primary-700',
  'screening_rejected': 'bg-gray-100 text-gray-700',
  'interview': 'bg-primary-100 text-primary-700',
  'interview_passed': 'bg-primary-100 text-primary-700',
  'offer': 'bg-primary-100 text-primary-700',
  'pending_onboard': 'bg-primary-100 text-primary-700',
  'onboarded': 'bg-primary-200 text-primary-800',
  'rejected': 'bg-gray-100 text-gray-700',
};

export default function ReferralPage({ currentUser, notificationBell, onOpenCandidate }: Props) {
  const [codeData, setCodeData] = useState<{ referral_code: string; career_token: string } | null>(null);
  const [referrals, setReferrals] = useState<any>(null);
  const [leaderboard, setLeaderboard] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showPoster, setShowPoster] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    Promise.all([
      getMyReferralCode(),
      getMyReferrals(),
      getReferralLeaderboard(),
    ]).then(([code, refs, board]) => {
      setCodeData(code);
      setReferrals(refs);
      setLeaderboard(board);
    }).catch(() => toast.error('加载内推数据失败'))
      .finally(() => setLoading(false));
  }, []);

  const baseUrl = window.location.origin;
  const referralUrl = codeData ? `${baseUrl}/career/${codeData.career_token}?ref=${codeData.referral_code}` : '';

  const copyLink = () => {
    navigator.clipboard.writeText(referralUrl).then(() => toast.success('内推链接已复制'));
  };

  // Generate poster using Canvas with real QR code
  const generatePoster = useCallback(async () => {
    if (!canvasRef.current || !codeData) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const W = 750, H = 1200;
    canvas.width = W;
    canvas.height = H;

    // Background gradient
    const grad = ctx.createLinearGradient(0, 0, 0, H);
    grad.addColorStop(0, '#2e6bf0');
    grad.addColorStop(0.6, '#1f54d6');
    grad.addColorStop(1, '#0a1633');
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, W, H);

    // Decorative circles
    ctx.beginPath();
    ctx.arc(600, 150, 200, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fill();
    ctx.beginPath();
    ctx.arc(100, 900, 300, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(255,255,255,0.03)';
    ctx.fill();

    // Title
    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 52px system-ui, -apple-system, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('加入我们', W / 2, 160);

    // Subtitle
    ctx.font = '28px system-ui, sans-serif';
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.fillText('一起创造更大的价值', W / 2, 220);

    // White card area
    const cardX = 50, cardY = 300, cardW = W - 100, cardH = 500;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.roundRect(cardX, cardY, cardW, cardH, 24);
    ctx.fill();

    // Card content
    ctx.fillStyle = '#1f2937';
    ctx.font = 'bold 36px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText('扫码查看热招岗位', W / 2, cardY + 60);

    ctx.fillStyle = '#6b7280';
    ctx.font = '22px system-ui, sans-serif';
    ctx.fillText('我们正在寻找优秀的你', W / 2, cardY + 100);

    // Generate real QR code as data URL and draw on canvas
    const qrSize = 260;
    const qrX = (W - qrSize) / 2, qrY = cardY + 130;
    try {
      const qrDataUrl = await QRCode.toDataURL(referralUrl, {
        width: qrSize,
        margin: 2,
        color: { dark: '#1f2937', light: '#ffffff' },
      });
      const qrImg = new Image();
      await new Promise<void>((resolve) => {
        qrImg.onload = () => {
          ctx.drawImage(qrImg, qrX, qrY, qrSize, qrSize);
          resolve();
        };
        qrImg.src = qrDataUrl;
      });
    } catch {
      // Fallback: draw placeholder
      ctx.fillStyle = '#f3f4f6';
      ctx.fillRect(qrX, qrY, qrSize, qrSize);
      ctx.fillStyle = '#9ca3af';
      ctx.font = '20px system-ui, sans-serif';
      ctx.fillText('QR Code', W / 2, qrY + qrSize / 2);
    }

    // Referrer info
    ctx.fillStyle = '#2e6bf0';
    ctx.font = 'bold 24px system-ui, sans-serif';
    ctx.textAlign = 'center';
    ctx.fillText(`${currentUser.display_name || currentUser.username} 邀请你`, W / 2, cardY + cardH - 60);

    ctx.fillStyle = '#9ca3af';
    ctx.font = '18px system-ui, sans-serif';
    ctx.fillText(`内推码: ${codeData.referral_code}`, W / 2, cardY + cardH - 25);

    // Bottom text
    ctx.fillStyle = 'rgba(255,255,255,0.7)';
    ctx.font = '20px system-ui, sans-serif';
    ctx.fillText('长按识别二维码 · 投递简历', W / 2, H - 100);

    ctx.fillStyle = 'rgba(255,255,255,0.4)';
    ctx.font = '16px system-ui, sans-serif';
    ctx.fillText('留形面试官 · 人才招聘平台', W / 2, H - 60);

    setShowPoster(true);
  }, [codeData, currentUser, referralUrl]);

  const downloadPoster = () => {
    if (!canvasRef.current) return;
    const link = document.createElement('a');
    link.download = `内推海报_${currentUser.display_name || 'referral'}.png`;
    link.href = canvasRef.current.toDataURL('image/png');
    link.click();
    toast.success('海报已保存');
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header */}
      <div className="shrink-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <Share2 size={20} className="text-primary-600" />
            我的内推
          </h1>
          <p className="text-sm text-gray-500 mt-0.5">生成专属海报，邀请人才加入</p>
        </div>
        {notificationBell}
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-5">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-xl font-bold text-primary-600">{referrals?.total || 0}</p>
            <p className="text-xs text-gray-500 mt-1">总内推人数</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-xl font-bold text-primary-600">{referrals?.passed || 0}</p>
            <p className="text-xs text-gray-500 mt-1">已进入面试+</p>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4 text-center">
            <p className="text-xl font-bold text-gray-600">
              {referrals?.total ? Math.round((referrals.passed / referrals.total) * 100) : 0}%
            </p>
            <p className="text-xs text-gray-500 mt-1">转化率</p>
          </div>
        </div>

        {/* Referral code + actions */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-medium text-gray-700">我的内推码</p>
              <p className="text-xl font-bold text-primary-600 font-mono mt-1">{codeData?.referral_code}</p>
            </div>
            <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center">
              <QrCode size={24} className="text-primary-600" />
            </div>
          </div>

          <div className="flex items-center gap-2 mb-4">
            <input value={referralUrl} readOnly
              className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 text-gray-600 font-mono truncate" />
            <button onClick={copyLink}
              className="px-3 py-2 text-xs font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 rounded-lg transition-colors flex items-center gap-1">
              <Copy size={12} /> 复制
            </button>
          </div>

          <div className="flex gap-2">
            <button onClick={generatePoster}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center justify-center gap-2">
              <QrCode size={16} /> 生成专属海报
            </button>
          </div>
        </div>

        {/* Poster preview */}
        {showPoster && (
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-700">我的内推海报</h3>
              <button onClick={downloadPoster}
                className="px-3 py-1.5 text-xs font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors flex items-center gap-1">
                <Download size={12} /> 保存到手机
              </button>
            </div>
            <div className="flex justify-center">
              <canvas ref={canvasRef} className="max-w-[300px] w-full rounded-lg shadow-lg" />
            </div>
            <p className="text-[11px] text-gray-400 text-center mt-3">长按海报保存，分享到朋友圈</p>
          </div>
        )}

        {/* My referrals list */}
        <div className="bg-white rounded-xl border border-gray-200">
          <div className="px-5 pt-4 pb-3">
            <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
              <Users size={16} className="text-primary-500" />
              我的内推记录
              <span className="text-xs font-normal text-gray-400">{referrals?.total || 0} 人</span>
            </h2>
          </div>
          {!referrals?.referrals?.length ? (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <Share2 size={32} strokeWidth={1} className="text-gray-300 mb-2" />
              <p className="text-sm">暂无内推记录</p>
              <p className="text-xs mt-1">分享海报开始内推吧！</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100 pb-2">
              {referrals.referrals.map((r: any) => (
                <div key={r.id}
                  className="flex items-center gap-3.5 px-5 py-3 hover:bg-gray-50/50 cursor-pointer transition-colors"
                  onClick={() => onOpenCandidate?.(r.id)}>
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center text-xs font-bold text-primary-600 shrink-0">
                    {(r.name || '?').charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[13px] font-bold text-gray-800">{r.name}</span>
                      {r.position_name && (
                        <span className="text-[10px] text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded-full">{r.position_name}</span>
                      )}
                    </div>
                    <div className="text-[11px] text-gray-400 mt-0.5">
                      {r.created_at?.slice(0, 10)}
                    </div>
                  </div>
                  <span className={clsx('text-[10px] px-2 py-0.5 rounded-full font-medium', STAGE_COLORS[r.stage] || 'bg-gray-100 text-gray-600')}>
                    {r.stage_label}
                  </span>
                  <ChevronRight size={14} className="text-gray-300 shrink-0" />
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Leaderboard */}
        {leaderboard.length > 0 && (
          <div className="bg-white rounded-xl border border-gray-200">
            <div className="px-5 pt-4 pb-3">
              <h2 className="text-sm font-semibold text-gray-700 flex items-center gap-2">
                <Award size={16} className="text-gray-500" />
                内推排行榜
              </h2>
            </div>
            <div className="divide-y divide-gray-100 pb-2">
              {leaderboard.map((item, idx) => (
                <div key={item.id} className="flex items-center gap-3 px-5 py-2.5">
                  <span className={clsx('w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-bold shrink-0',
                    idx === 0 ? 'bg-gray-100 text-gray-700' :
                    idx === 1 ? 'bg-gray-200 text-gray-600' :
                    idx === 2 ? 'bg-gray-100 text-gray-600' : 'bg-gray-50 text-gray-400'
                  )}>{idx + 1}</span>
                  <span className="text-sm text-gray-800 flex-1">{item.display_name}</span>
                  <span className="text-xs text-gray-500">{item.referral_count} 推荐</span>
                  <span className="text-xs text-primary-600 font-medium">{item.passed_count} 通过</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

